import type { MenuProps } from 'antd/es/menu'

type MenuItem = NonNullable<MenuProps['items']>[number]
type BranchItem = { key: string; label?: unknown; children?: MenuItem[] }

/**
 * Chia cây menu theo `layout` / `splitMenus` / `siderMenuType`. Hàm thuần, không đụng
 * `buildMenuFromTabs` — quyền hiển thị vẫn quyết ở đó, đây chỉ là tầng trình bày.
 */

const asBranch = (item: MenuItem): BranchItem | null =>
  item && typeof item === 'object' && 'key' in item ? (item as BranchItem) : null

/** Cấp 1, bỏ con — menu ngang của mode `mix`. */
export const topLevelItems = (items: MenuItem[]): MenuItem[] =>
  items.map((item) => {
    const branch = asBranch(item)
    if (!branch?.children) return item
    const { children, ...rest } = branch
    return rest as MenuItem
  })

const contains = (nodes: MenuItem[], key: string): boolean =>
  nodes.some((node) => {
    const branch = asBranch(node)
    if (!branch) return false
    return branch.key === key || (branch.children ? contains(branch.children, key) : false)
  })

/** Key cấp 1 đang hoạt động: nhánh chứa `selectedKey`, hoặc chính nó nếu nó ở cấp 1. */
export const topKeyOfSelected = (items: MenuItem[], selectedKey?: string): string | undefined => {
  if (!selectedKey) return undefined
  for (const item of items) {
    const branch = asBranch(item)
    if (!branch) continue
    if (branch.key === selectedKey) return branch.key
    if (branch.children && contains(branch.children, selectedKey)) return branch.key
  }
  return undefined
}

/** Con của nhánh cấp 1 ứng với `key` (chính nó hoặc nhánh chứa nó). Không có → rỗng. */
export const childrenOfActive = (items: MenuItem[], key?: string): MenuItem[] => {
  if (!key) return []
  for (const item of items) {
    const branch = asBranch(item)
    if (!branch?.children) continue
    if (branch.key === key || contains(branch.children, key)) return branch.children
  }
  return []
}

/** Nhánh cấp 1 thành group phẳng (antd `type: 'group'`). Group header không nhận icon. */
export const toGroupItems = (items: MenuItem[]): MenuItem[] =>
  items.map((item) => {
    const branch = asBranch(item)
    if (!branch?.children) return item
    return {
      key: branch.key,
      type: 'group',
      label: branch.label,
      children: branch.children,
    } as MenuItem
  })
